import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PanelLayout from '../CommanderOffline/PanelLayout';
import useTypeEntity from '../../../useTypeEntity';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../../../../../../service/store/Store.constants';
import useTypeRequest from '../../useTypeRequest';
import StructureListItem from '../../../StructureListItem';
import { DeviceConnectionErrorIcon } from '../../../../../../../../assets/dashboard';
import useWatchError from '../../../../../../../../shared/hooks/useWatchError';
import useStructures from '../../../../../../../../redux/structure/hooks/useStructures';
import {
  BusinessLayerGroupType,
  getTypeFromGroup,
} from '../../../../../../../../redux/structure/util/getTypeFromGroup';
import { getGroupsFromDeviceIdsGroupDictionary } from '../../../../../../../../redux/structure/selectors/Group.selector';
import { toBase64 } from '../../../../../../../../shared/util/base64';
import useAirConnectStatus from '../../../../../../../../service/device/CommanderAPI/hooks/useAirConnectStatus';
import getAirConnectNodeError from '../../../../../../../../service/device/CommanderAPI/util/getAirConnectNodeError';

function FlexeserveDeviceConnectionErrorTableList() {
  const { groupId, marketId } = useTypeEntity({
    pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
  });
  const { t } = useTranslation();
  const typeRequest = useTypeRequest(marketId, groupId);

  const {
    data: dataAirConnectStatus,
    isLoading: isLoadingDataAirConnect,
    error: errorAirConnect,
  } = useAirConnectStatus({
    groupId,
    workspaceId: marketId,
    typeRequest,
  });

  const {
    data: structuresData,
    isLoading: isLoadingStructures,
    error: errorStructures,
    originalArgs: structuresOriginalArgs,
  } = useStructures({
    typeRequest,
    groupId,
    workspaceId: marketId,
  });

  // Use getAirConnectNodeError to filter device errors
  const nodeErrorDevices = useMemo(
    () =>
      getAirConnectNodeError(dataAirConnectStatus)?.map(
        (item) => item.deviceID
      ) ?? [],
    [dataAirConnectStatus]
  );

  const childGroups = useMemo(
    () =>
      structuresData?.flatMap((structure, i) =>
        (groupId
          ? structure.entities?.groups[toBase64(groupId)]?.childGroups ?? []
          : structure.result
        )
          .flatMap((groupId) => {
            const groupsWithDeviceError = getGroupsFromDeviceIdsGroupDictionary(
              structure.entities?.groups ?? {},
              nodeErrorDevices,
              groupId
            );
            return groupsWithDeviceError.filter(
              (group) =>
                getTypeFromGroup(group) === BusinessLayerGroupType.Store
            );
          })
          .map((group) => ({
            ...group,
            workspaceId: marketId ?? structuresOriginalArgs[i],
          }))
      ),
    [
      nodeErrorDevices,
      groupId,
      marketId,
      structuresData,
      structuresOriginalArgs,
    ]
  );

  const loading = isLoadingDataAirConnect || isLoadingStructures;
  const error = errorAirConnect || errorStructures;

  useWatchError(error);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const childGroupsPaginated = useMemo(
    () =>
      childGroups?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ) ?? [],
    [childGroups, page, rowsPerPage]
  );

  const content = loading ? (
    <Skeleton variant="rectangular" width={'100%'} height={200} />
  ) : (
    <Box mt={2}>
      <Typography variant="body1" gutterBottom>
        {t('dashboard.flexeserve_device_connection_error.description_list')}
      </Typography>
      {childGroupsPaginated.length === 0 ? (
        <Alert severity="info">
          {t('dashboard.flexeserve_device_connection_error.no_stores')}
        </Alert>
      ) : (
        <>
          <Stack spacing={1} mt={2}>
            {childGroupsPaginated.map((entity: any) => (
              <StructureListItem key={entity?.id} entity={entity} />
            ))}
          </Stack>
          <TablePagination
            component="div"
            count={childGroups?.length ?? 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
  return (
    <PanelLayout
      innerContainerMaxWidth={'xl'}
      subPages={[
        {
          name: t('dashboard.flexeserve_device_connection_error.title'),
          url: '',
        },
      ]}
      title={t('dashboard.flexeserve_device_connection_error.title')}
      icon={<DeviceConnectionErrorIcon />}
    >
      {content}
    </PanelLayout>
  );
}

export default FlexeserveDeviceConnectionErrorTableList;
